/**
 * Friendly names for clipboard-pasted images.
 *
 * The clipboard hands screenshots over as a generic `image.png` (Chrome and
 * WebKit alike); without intervention they land in the user's files as the
 * server's `inline-<timestamp>` mint. The rename happens HERE, at paste time,
 * because the client owns the only truthful local clock — the server runs UTC
 * and would stamp a wrong-looking date/time for every non-UTC user.
 *
 * Meaningful names (a real file copied in Finder and pasted) pass through
 * untouched, as do non-image files.
 */

const IMAGE_EXT: Record<string, string> = {
  "image/png": ".png",
  "image/jpeg": ".jpg",
  "image/gif": ".gif",
  "image/webp": ".webp",
};

/** Names the clipboard invents for pasted bitmaps — safe to replace. */
const GENERIC_NAME = /^(image|blob)(\.(png|jpe?g|gif|webp|avif))?$/i;

export function namePastedImages(
  files: File[],
  now: Date = new Date()
): File[] {
  const pad = (n: number) => String(n).padStart(2, "0");
  const stamp =
    `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}` +
    ` at ${pad(now.getHours())}.${pad(now.getMinutes())}.${pad(
      now.getSeconds()
    )}`;
  let minted = 0;
  return files.map((f) => {
    if (!f.type.startsWith("image/")) return f;
    if (f.name !== "" && !GENERIC_NAME.test(f.name)) return f;
    minted += 1;
    const suffix = minted > 1 ? ` (${minted})` : "";
    const ext = IMAGE_EXT[f.type] ?? ".png";
    return new File([f], `Pasted image ${stamp}${suffix}${ext}`, {
      type: f.type,
      lastModified: f.lastModified,
    });
  });
}
